import { create } from 'zustand';

import type { CrumbOverrides } from './crumbs';

/**
 * Lets a route supply `CrumbOverrides` for the address it is currently
 * rendering, without threading a prop through `AppShell`/`Topbar` down to
 * the ambient `Breadcrumbs` (spec 115 design doc §4.2, research.md Decision
 * 6) — the shell renders `Breadcrumbs` once, above every route, so a route
 * cannot pass it props directly the way it could a child it renders itself.
 *
 * Keyed by the pathname the overrides describe, not just "the last route to
 * write": `Breadcrumbs` only ever applies overrides that name the *current*
 * pathname, so a stale write from a route already left (e.g. an in-flight
 * fetch that resolves after navigating away) can never leak onto the next
 * page — the same reasoning `keepWhileSwitchingHidden` gives for not
 * carrying data across an identity change.
 *
 * In memory only, like `boardSelectionStore`: this is a moment's fact about
 * what is on screen, never a view to share or restore.
 */
export interface BreadcrumbOverridesState {
  pathname: string | null;
  overrides: CrumbOverrides | null;
  set: (pathname: string, overrides: CrumbOverrides) => void;
  /** Only clears if `pathname` still matches — see the keying note above. */
  clear: (pathname: string) => void;
}

export const useBreadcrumbOverridesStore = create<BreadcrumbOverridesState>()(
  (set, get) => ({
    pathname: null,
    overrides: null,
    set: (pathname, overrides) => {
      set({ pathname, overrides });
    },
    clear: (pathname) => {
      if (get().pathname === pathname) set({ pathname: null, overrides: null });
    },
  }),
);
